// Shared modal scaffolding + form primitives for the agent-pro flows.
// Mirrors the design's ModalShell/Field/Btn/Chip/AIHint/Tab vocabulary
// so the eight flow modals stay visually consistent.

import type { CSSProperties, ReactNode } from "react";
import { useEffect } from "react";

export function ModalShell({
  open,
  onClose,
  children,
  width = 720,
  label,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  width?: number;
  label?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(15, 22, 19, 0.42)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
      data-screen-label={label}
      // Marker for sibling Esc handlers (e.g. CaseAIRail) to detect
      // an open modal and yield Escape to it. Selector queried via
      // `document.querySelector('[data-modal-open="true"]')`.
      data-modal-open="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          maxWidth: "100%",
          maxHeight: "92vh",
          overflow: "hidden",
          background: "var(--ag-bg)",
          borderRadius: 14,
          boxShadow: "0 30px 80px rgba(0,0,0,0.35), 0 0 0 1px var(--ag-rule)",
          display: "flex",
          flexDirection: "column",
          fontFamily: "var(--ag-font-ui)",
          color: "var(--ag-ink)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function ModalHeader({
  eyebrow,
  title,
  hint,
  onClose,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  hint?: ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        padding: "20px 26px 18px",
        borderBottom: "1px solid var(--ag-rule)",
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
      }}
    >
      <div style={{ flex: 1 }}>
        {eyebrow && (
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              color: "var(--ag-accent)",
              marginBottom: 6,
            }}
          >
            {eyebrow}
          </div>
        )}
        <div
          style={{
            fontFamily: "var(--ag-font-serif)",
            fontSize: 22,
            fontWeight: 500,
            lineHeight: 1.15,
            letterSpacing: -0.2,
          }}
        >
          {title}
        </div>
        {hint && (
          <div style={{ fontSize: 12.5, color: "var(--ag-ink-3)", marginTop: 4 }}>{hint}</div>
        )}
      </div>
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          background: "transparent",
          border: "1px solid var(--ag-rule)",
          cursor: "pointer",
          color: "var(--ag-ink-3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M2 2l8 8M10 2l-8 8"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}

export function ModalBody({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        padding: "22px 26px",
        overflow: "auto",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 14,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function ModalFooter({
  children,
  leftHint,
}: {
  children: ReactNode;
  leftHint?: ReactNode;
}) {
  return (
    <div
      style={{
        padding: "14px 22px",
        borderTop: "1px solid var(--ag-rule)",
        background: "var(--ag-surface-2)",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <div style={{ flex: 1, fontSize: 11.5, color: "var(--ag-ink-3)" }}>{leftHint}</div>
      {children}
    </div>
  );
}

// ── Form primitives ────────────────────────────────────────────

export function Field({
  label,
  hint,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: "var(--ag-ink-3)",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
      {hint && (
        <div style={{ fontSize: 11.5, color: "var(--ag-ink-3)", marginTop: 5 }}>{hint}</div>
      )}
    </label>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  mono,
  type = "text",
  step,
}: {
  value?: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
  // Allow native input types (date / time / number). Defaults to
  // "text" so the existing free-form callers don't change behaviour.
  type?: string;
  step?: string;
}) {
  return (
    <input
      type={type}
      step={step}
      value={value || ""}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      style={{
        width: "100%",
        padding: "10px 12px",
        borderRadius: 8,
        border: "1px solid var(--ag-rule-strong)",
        background: "var(--ag-surface)",
        fontFamily: mono ? "var(--ag-font-mono)" : "var(--ag-font-ui)",
        fontSize: 13.5,
        color: "var(--ag-ink)",
        outline: "none",
      }}
    />
  );
}

export function TextArea({
  value,
  onChange,
  placeholder,
  rows = 4,
}: {
  value?: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      value={value || ""}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: "100%",
        padding: "10px 12px",
        borderRadius: 8,
        resize: "vertical",
        border: "1px solid var(--ag-rule-strong)",
        background: "var(--ag-surface)",
        fontFamily: "var(--ag-font-serif)",
        fontSize: 14,
        lineHeight: 1.5,
        color: "var(--ag-ink)",
        outline: "none",
      }}
    />
  );
}

export function Select({
  value,
  onChange,
  options,
}: {
  value?: string;
  onChange?: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange?.(e.target.value)}
      style={{
        width: "100%",
        padding: "10px 12px",
        borderRadius: 8,
        border: "1px solid var(--ag-rule-strong)",
        background: "var(--ag-surface)",
        fontSize: 13.5,
        color: "var(--ag-ink)",
        fontFamily: "var(--ag-font-ui)",
      }}
    >
      <option value="">—</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

export function Btn({
  children,
  primary,
  ghost,
  onClick,
  disabled,
  icon,
}: {
  children: ReactNode;
  primary?: boolean;
  ghost?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  icon?: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "8px 14px",
        borderRadius: 8,
        background: primary
          ? "var(--ag-accent)"
          : ghost
            ? "transparent"
            : "var(--ag-surface)",
        color: primary ? "var(--ag-bg)" : "var(--ag-ink)",
        border: ghost
          ? "none"
          : primary
            ? "1px solid var(--ag-accent)"
            : "1px solid var(--ag-rule-strong)",
        fontSize: 12.5,
        fontWeight: 600,
        fontFamily: "var(--ag-font-ui)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
      }}
    >
      {icon}
      {children}
    </button>
  );
}

export function Chip({
  active,
  onClick,
  children,
  icon,
  large,
}: {
  active?: boolean;
  onClick?: () => void;
  children: ReactNode;
  icon?: ReactNode;
  large?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: large ? "12px 16px" : "7px 12px",
        borderRadius: large ? 10 : 999,
        background: active ? "var(--ag-accent-soft)" : "var(--ag-surface)",
        border: `1px solid ${active ? "var(--ag-accent)" : "var(--ag-rule-strong)"}`,
        color: active ? "var(--ag-accent)" : "var(--ag-ink-2)",
        fontSize: large ? 13 : 12,
        fontWeight: 600,
        fontFamily: "var(--ag-font-ui)",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      {icon}
      {children}
    </button>
  );
}

export function AIHint({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: "10px 12px",
        borderRadius: 8,
        background: "var(--ag-ai-bg)",
        border: "1px solid var(--ag-ai-rule)",
        fontSize: 12.5,
        color: "var(--ag-ai-accent)",
        lineHeight: 1.5,
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
      }}
    >
      <span style={{ marginTop: 2, flexShrink: 0 }}>
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
          <path d="M7 1l1.5 4L13 7l-4.5 1.5L7 13 5.5 8.5 1 7l4.5-1.5L7 1z" fill="currentColor" />
        </svg>
      </span>
      <span>{children}</span>
    </div>
  );
}

export function Tab({
  active,
  onClick,
  children,
}: {
  active?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "12px 4px",
        margin: "0 14px 0 0",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        fontFamily: "var(--ag-font-ui)",
        fontSize: 12.5,
        fontWeight: 600,
        letterSpacing: 0.2,
        color: active ? "var(--ag-ink)" : "var(--ag-ink-3)",
        borderBottom: `2px solid ${active ? "var(--ag-accent)" : "transparent"}`,
        marginBottom: -1,
      }}
    >
      {children}
    </button>
  );
}

export function Section({
  title,
  hint,
  children,
}: {
  title: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: "var(--ag-ink-3)",
          }}
        >
          {title}
        </span>
        {hint && <span style={{ fontSize: 11, color: "var(--ag-ink-4)" }}>· {hint}</span>}
      </div>
      {children}
    </div>
  );
}
